import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeft, Upload, FileText, RefreshCw, Trash2, ExternalLink,
  AlertTriangle, CreditCard, Calendar, TrendingUp, DollarSign, Clock,
} from 'lucide-react';
import { colors, API, fmt, faseColor, riscoColor, escalacaoLabel, statusAcordoColor, authHeaders } from '../utils/theme';
import {
  Table, Th, Td, TrHover, Badge, LoadingSpinner, EmptyState, Button, Modal, FormField, Input, Select,
} from '../components/UI';

const mesAtual = () => new Date().toISOString().slice(0, 7);

const LABELS_CAT = {
  JUROS_ENCARGOS_IOF: 'Juros/Encargos/IOF',
  TAXA_COMPANHIA: 'Taxa/Companhia',
  COMPRA_PONTOS: 'Compra de Pontos',
  PARCELA_FINANCIADA: 'Parcela Financiada',
  OUTROS: 'Outros',
};
const CORES_CAT = {
  JUROS_ENCARGOS_IOF: '#ef4444',
  TAXA_COMPANHIA: '#eab308',
  COMPRA_PONTOS: '#3b82f6',
  PARCELA_FINANCIADA: '#f97316',
  OUTROS: '#6b7280',
};

function CatBadge({ cat }) {
  const label = LABELS_CAT[cat] || cat || '—';
  const cor = CORES_CAT[cat] || colors.textDim;
  return (
    <span style={{
      background: cor + '22', color: cor, border: `1px solid ${cor}44`,
      borderRadius: 4, padding: '2px 7px', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

function InfoPill({ label, value, color }) {
  return (
    <div style={{ background: colors.bg, borderRadius: 8, padding: '10px 14px', border: `1px solid ${colors.border}`, minWidth: 120 }}>
      <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || colors.text }}>{value || '—'}</div>
    </div>
  );
}

const TABS = [
  { id: 'resumo',      label: '📊 Resumo' },
  { id: 'faturas',     label: '📄 Faturas' },
  { id: 'lancamentos', label: '📋 Lançamentos' },
  { id: 'negociacoes', label: '🤝 Negociações' },
  { id: 'contas',      label: '💰 Contas a Pagar' },
  { id: 'documentacao', label: '📎 Documentação' },
];

export default function CriseCartaoDetalhePage({ cartaoId, onBack }) {
  const [cartao, setCartao]           = useState(null);
  const [faturas, setFaturas]         = useState({ uploads: [], por_mes: [] });
  const [transacoes, setTransacoes]   = useState([]);
  const [contasPagar, setContasPagar] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('resumo');

  // Filtros lançamentos
  const [filtroMes, setFiltroMes]     = useState('');
  const [filtroCat, setFiltroCat]     = useState('');

  // Upload modal
  const [uploadModal, setUploadModal]   = useState(false);
  const [uploadMes, setUploadMes]       = useState(mesAtual());
  const [uploadFile, setUploadFile]     = useState(null);
  const [uploadSenha, setUploadSenha]   = useState('');
  const [uploadValorManual, setUploadValorManual] = useState('');
  const [uploading, setUploading]       = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  // Documentação de cartão sem fatura (chamado #11)
  const [comprovantes, setComprovantes] = useState([]);
  const [compForm, setCompForm] = useState({ descricao: '', valor_divida: '', desconto: '', mes_referencia: mesAtual(), data_comprovante: '', file: null });
  const [savingComp, setSavingComp] = useState(false);
  const [togglingSemFatura, setTogglingSemFatura] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cardRes, fatRes, txRes, capRes, compRes] = await Promise.all([
        fetch(`${API}/cartoes/${cartaoId}`, { headers: authHeaders() }),
        fetch(`${API}/faturas/cartao/${cartaoId}`),
        fetch(`${API}/transacoes?cartao_id=${cartaoId}&incluir_negociacao=true&limit=500`),
        fetch(`${API}/contas-a-pagar?cartao_id=${cartaoId}&limit=200`),
        fetch(`${API}/cartoes/${cartaoId}/comprovantes`, { headers: authHeaders() }),
      ]);
      const [card, fat, txData, cap, comp] = await Promise.all([
        cardRes.json(), fatRes.json(), txRes.json(), capRes.json(), compRes.json(),
      ]);
      // Defensive: só atualiza estado se a resposta é válida (evita crash no render)
      if (card && !card.error) setCartao(card);
      setFaturas(fat?.uploads ? fat : { uploads: [], por_mes: [] });
      setTransacoes(Array.isArray(txData?.data) ? txData.data : Array.isArray(txData) ? txData : []);
      setContasPagar(Array.isArray(cap?.data) ? cap.data : Array.isArray(cap) ? cap : []);
      setComprovantes(Array.isArray(comp) ? comp : []);
    } catch (e) { console.error('load error:', e); }
    setLoading(false);
  }, [cartaoId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async () => {
    if (!uploadFile || !uploadMes) return alert('Selecione o arquivo e o mês');
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('arquivo', uploadFile);
      fd.append('cartao_id', cartaoId);
      fd.append('titular_id', cartao?.titular_id);
      fd.append('mes_referencia', uploadMes);
      if (uploadSenha.trim()) fd.append('senha_pdf', uploadSenha.trim());
      if (uploadValorManual.trim()) {
        // Aceita "1.234,56" ou "1234.56"
        const v = uploadValorManual.trim().replace(/\./g, '').replace(',', '.');
        fd.append('valor_fatura_manual', v);
      }
      const res = await fetch(`${API}/faturas/upload`, { method: 'POST', headers: authHeaders(), body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erro');
      setUploadResult(data);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
    setUploading(false);
  };

  const reprocessar = async (cartaoId, mes) => {
    try {
      const res = await fetch(`${API}/faturas/processar/${cartaoId}/${mes}`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      alert(`✓ ${data.total_transacoes} lançamentos extraídos`);
      load();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  const excluirUpload = async (id) => {
    if (!window.confirm('Excluir este upload e seus lançamentos?')) return;
    await fetch(`${API}/faturas/upload/${id}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  // ── Documentação / cartão sem fatura (chamado #11) ──────────────────────────
  const toggleSemFatura = async () => {
    const novo = !cartao?.sem_fatura;
    if (novo && !window.confirm('Marcar este cartão como "parou de gerar fatura"?\n\nEle sai do controle de faturas (não vai mais pedir fatura). O acompanhamento passa a ser por comprovantes aqui.')) return;
    setTogglingSemFatura(true);
    try {
      const r = await fetch(`${API}/cartoes/${cartaoId}/sem-fatura`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ sem_fatura: novo }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setCartao(c => ({ ...c, sem_fatura: novo }));
    } catch (e) { alert('Erro: ' + e.message); }
    setTogglingSemFatura(false);
  };

  const addComprovante = async () => {
    if (!compForm.descricao.trim() && !compForm.file) return alert('Informe uma descrição ou anexe um arquivo.');
    setSavingComp(true);
    try {
      const fd = new FormData();
      fd.append('descricao', compForm.descricao || '');
      fd.append('valor_divida', compForm.valor_divida || '0');
      fd.append('desconto', compForm.desconto || '0');
      fd.append('mes_referencia', compForm.mes_referencia || '');
      fd.append('data_comprovante', compForm.data_comprovante || '');
      if (compForm.file) fd.append('arquivo', compForm.file);
      const r = await fetch(`${API}/cartoes/${cartaoId}/comprovantes`, { method: 'POST', headers: authHeaders(), body: fd });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.error || `HTTP ${r.status}`); }
      setCompForm({ descricao: '', valor_divida: '', desconto: '', mes_referencia: mesAtual(), data_comprovante: '', file: null });
      load();
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
    setSavingComp(false);
  };

  const delComprovante = async (id) => {
    if (!window.confirm('Remover este comprovante?')) return;
    await fetch(`${API}/cartoes/${cartaoId}/comprovantes/${id}`, { method: 'DELETE', headers: authHeaders() });
    load();
  };

  if (loading) return <LoadingSpinner />;
  if (!cartao) return <EmptyState message="Cartão não encontrado" icon={CreditCard} />;

  // Meses disponíveis para filtro
  const mesesDisp = [...new Set(transacoes.map(t => t.mes_referencia).filter(Boolean))].sort().reverse();

  const txFiltradas = transacoes.filter(t => {
    if (filtroMes && t.mes_referencia !== filtroMes) return false;
    if (filtroCat && t.categoria !== filtroCat) return false;
    return true;
  });

  // KPIs por categoria (total geral)
  const totalJuros    = transacoes.reduce((s, t) => t.categoria === 'JUROS_ENCARGOS_IOF' ? s + (t.valor || 0) : s, 0);
  const totalParcelas = transacoes.reduce((s, t) => t.categoria === 'PARCELA_FINANCIADA'  ? s + (t.valor || 0) : s, 0);
  const totalTaxas    = transacoes.reduce((s, t) => t.categoria === 'TAXA_COMPANHIA'       ? s + (t.valor || 0) : s, 0);
  const totalPontos   = transacoes.reduce((s, t) => t.categoria === 'COMPRA_PONTOS'        ? s + (t.valor || 0) : s, 0);

  const tabStyle = (id) => ({
    padding: '9px 18px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
    borderBottom: activeTab === id ? `2px solid ${colors.primary}` : '2px solid transparent',
    background: 'none', color: activeTab === id ? colors.primary : colors.textMuted,
    transition: 'all 0.15s',
  });

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} style={{
        display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
        cursor: 'pointer', color: colors.textMuted, fontSize: 13, marginBottom: 16, padding: 0,
      }}>
        <ArrowLeft size={15} /> Voltar para Gestão de Crise
      </button>

      {/* ===== HEADER ===== */}
      <div style={{ background: colors.card, borderRadius: 12, padding: 24, marginBottom: 20, border: `1px solid ${colors.border}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>{cartao.nome_cartao}</h2>
              {cartao.gerenciamento_crise === 'SIM' && (
                <span style={{ background: colors.warning + '22', color: colors.warning, border: `1px solid ${colors.warning}55`, borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                  EM NEGOCIAÇÃO
                </span>
              )}
              {cartao.judicializado === 'SIM' && (
                <span style={{ background: '#ef444422', color: '#ef4444', border: '1px solid #ef444444', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>
                  JUDICIALIZADO
                </span>
              )}
            </div>
            <div style={{ color: colors.textMuted, fontSize: 13 }}>
              {cartao.titular_nome} &nbsp;·&nbsp; {cartao.banco_nome || 'Sem banco'} &nbsp;·&nbsp;
              {cartao.bandeira || ''} {cartao.numeracao_final ? `*${cartao.numeracao_final}` : ''}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: colors.danger }}>{fmt(cartao.fatura_atual)}</div>
            <div style={{ fontSize: 12, color: colors.textMuted }}>Fatura atual</div>
          </div>
        </div>

        {/* Pills de status */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
          <InfoPill label="Limite" value={fmt(cartao.limite_total)} />
          <InfoPill label="Disponível" value={fmt(cartao.limite_disponivel)} />
          <InfoPill label="Pgto Mínimo" value={fmt(cartao.pgto_minimo)} />
          <InfoPill label="Dias Atraso" value={cartao.dias_atraso > 0 ? `${cartao.dias_atraso}d` : 'Em dia'}
            color={cartao.dias_atraso >= 90 ? colors.danger : cartao.dias_atraso > 0 ? colors.warning : colors.success} />
          <InfoPill label="Fase" value={cartao.fase || 'EM DIA'} color={faseColor(cartao.fase)} />
          <InfoPill label="Risco" value={cartao.risco || 'BAIXO'} color={riscoColor(cartao.risco)} />
          <InfoPill label="Escalação" value={escalacaoLabel(cartao.nivel_escalacao)}
            color={cartao.nivel_escalacao > 0 ? colors.warning : colors.textDim} />
          <InfoPill label="Acordo" value={(cartao.status_acordo || 'SEM_ACORDO').replace('_', ' ')}
            color={statusAcordoColor(cartao.status_acordo)} />
          {cartao.serasa === 'SIM' && <InfoPill label="Serasa" value="SIM" color={colors.danger} />}
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, marginBottom: 20, gap: 0 }}>
        {TABS.map(t => <button key={t.id} style={tabStyle(t.id)} onClick={() => setActiveTab(t.id)}>{t.label}</button>)}
      </div>

      {/* ===== RESUMO ===== */}
      {activeTab === 'resumo' && (
        <div>
          {/* KPIs por categoria */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 24 }}>
            {[
              { label: 'Juros/Encargos/IOF', value: totalJuros,    color: '#ef4444' },
              { label: 'Parcelas Financiadas', value: totalParcelas, color: '#f97316' },
              { label: 'Taxas/Companhia',      value: totalTaxas,   color: '#eab308' },
              { label: 'Compra de Pontos',      value: totalPontos,  color: '#3b82f6' },
            ].map(k => (
              <div key={k.label} style={{
                background: colors.card, borderRadius: 10, padding: '14px 18px',
                border: `1px solid ${k.color}33`, flex: '1 1 180px',
              }}>
                <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>{k.label}</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{fmt(k.value)}</div>
              </div>
            ))}
          </div>

          {/* Histórico de fatura por mês */}
          {faturas.por_mes.length > 0 && (
            <>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Histórico por Mês</h3>
              <Table>
                <thead><tr>
                  <Th>Mês</Th><Th>Lançamentos</Th><Th>Juros/IOF</Th><Th>Parcelas</Th><Th>Taxas</Th><Th>Pontos</Th><Th>Total</Th>
                </tr></thead>
                <tbody>
                  {faturas.por_mes.map(m => (
                    <TrHover key={m.mes_referencia}>
                      <Td style={{ fontWeight: 600 }}>{m.mes_referencia || '—'}</Td>
                      <Td style={{ textAlign: 'center' }}>{m.total_lancamentos}</Td>
                      <Td style={{ color: '#ef4444', fontWeight: 600 }}>{fmt(m.juros_encargos)}</Td>
                      <Td style={{ color: '#f97316' }}>{fmt(m.parcelas)}</Td>
                      <Td style={{ color: '#eab308' }}>{fmt(m.taxas)}</Td>
                      <Td style={{ color: '#3b82f6' }}>{fmt(m.pontos)}</Td>
                      <Td style={{ fontWeight: 700 }}>{fmt(m.valor_total)}</Td>
                    </TrHover>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      )}

      {/* ===== FATURAS ===== */}
      {activeTab === 'faturas' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Faturas Enviadas ({faturas.uploads.length})</h3>
            <Button onClick={() => { setUploadModal(true); setUploadResult(null); setUploadFile(null); }}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Upload size={14} /> Enviar Fatura
            </Button>
          </div>

          {faturas.uploads.length === 0 ? (
            <EmptyState message="Nenhuma fatura enviada" icon={FileText} />
          ) : (
            <Table>
              <thead><tr>
                <Th>Mês</Th><Th>Arquivo</Th><Th>Status</Th><Th>Lançamentos</Th><Th>Valor Extraído</Th><Th>Enviado em</Th><Th align="center">Ações</Th>
              </tr></thead>
              <tbody>
                {faturas.uploads.map(u => (
                  <TrHover key={u.id}>
                    <Td style={{ fontWeight: 700 }}>{u.mes_referencia}</Td>
                    <Td style={{ fontSize: 12, color: colors.textMuted }}>{u.nome_arquivo}</Td>
                    <Td>
                      <Badge
                        text={u.status}
                        color={u.status === 'PROCESSADO' ? colors.success : u.status === 'ENVIADO' ? colors.info : colors.warning}
                      />
                    </Td>
                    <Td style={{ textAlign: 'center' }}>{u.total_transacoes ?? '—'}</Td>
                    <Td style={{ fontWeight: 600, color: colors.danger }}>{u.valor_total_fatura ? fmt(u.valor_total_fatura) : '—'}</Td>
                    <Td style={{ fontSize: 12, color: colors.textDim }}>
                      {u.data_upload ? new Date(u.data_upload).toLocaleDateString('pt-BR') : '—'}
                    </Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <a href={`${API}/faturas/pdf/${cartaoId}/${u.mes_referencia}?token=${encodeURIComponent(localStorage.getItem('xm_token') || '')}`} target="_blank" rel="noreferrer"
                          style={{ display: 'flex', alignItems: 'center', gap: 3, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 4, padding: '3px 8px', color: colors.text, fontSize: 11, textDecoration: 'none' }}>
                          <ExternalLink size={11} /> PDF
                        </a>
                        <button onClick={() => reprocessar(cartaoId, u.mes_referencia)}
                          style={{ display: 'flex', alignItems: 'center', gap: 3, background: `${colors.primary}22`, border: `1px solid ${colors.primary}44`, borderRadius: 4, padding: '3px 8px', color: colors.primary, fontSize: 11, cursor: 'pointer' }}>
                          <RefreshCw size={11} /> Reprocessar
                        </button>
                        <button onClick={() => excluirUpload(u.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 3, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, borderRadius: 4, padding: '3px 8px', color: colors.danger, fontSize: 11, cursor: 'pointer' }}>
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </Td>
                  </TrHover>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* ===== LANÇAMENTOS ===== */}
      {activeTab === 'lancamentos' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
              style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
              <option value="">Todos os meses</option>
              {mesesDisp.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <select value={filtroCat} onChange={e => setFiltroCat(e.target.value)}
              style={{ background: colors.input, color: colors.text, border: `1px solid ${colors.border}`, borderRadius: 6, padding: '6px 12px', fontSize: 13 }}>
              <option value="">Todas categorias</option>
              {Object.entries(LABELS_CAT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <span style={{ color: colors.textMuted, fontSize: 13, alignSelf: 'center' }}>
              {txFiltradas.length} lançamentos · {fmt(txFiltradas.reduce((s, t) => s + (t.valor || 0), 0))}
            </span>
          </div>

          {txFiltradas.length === 0 ? (
            <EmptyState message="Nenhum lançamento" icon={FileText} />
          ) : (
            <Table>
              <thead><tr>
                <Th>Data</Th><Th>Descrição</Th><Th>Categoria</Th><Th>Mês Ref.</Th><Th>Parcela</Th><Th>Valor</Th><Th>Origem</Th>
              </tr></thead>
              <tbody>
                {txFiltradas.map(t => (
                  <TrHover key={t.id}>
                    <Td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{t.data_compra || t.data_vencimento || '—'}</Td>
                    <Td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.descricao}>{t.descricao}</Td>
                    <Td><CatBadge cat={t.categoria} /></Td>
                    <Td style={{ fontSize: 12 }}>{t.mes_referencia || '—'}</Td>
                    <Td style={{ fontSize: 12, color: colors.textDim }}>{t.parcela || '—'}</Td>
                    <Td style={{ fontWeight: 600, color: (t.valor || 0) < 0 ? colors.success : colors.danger }}>{fmt(Math.abs(t.valor || 0))}</Td>
                    <Td>
                      <span style={{ fontSize: 10, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 3, padding: '1px 5px', color: colors.textDim }}>
                        {t.origem}
                      </span>
                    </Td>
                  </TrHover>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* ===== NEGOCIAÇÕES ===== */}
      {activeTab === 'negociacoes' && (
        <div>
          {(!cartao.negociacoes || cartao.negociacoes.length === 0) ? (
            <EmptyState message="Nenhuma negociação registrada" icon={AlertTriangle} />
          ) : (
            <Table>
              <thead><tr>
                <Th>Rodada</Th><Th>Data</Th><Th>Canal</Th><Th>Protocolo</Th>
                <Th>Proposta</Th><Th>Desconto</Th><Th>Parcelas</Th><Th>Resultado</Th><Th>Próxima Ação</Th>
              </tr></thead>
              <tbody>
                {cartao.negociacoes.map(n => (
                  <TrHover key={n.id}>
                    <Td style={{ textAlign: 'center', fontWeight: 700 }}>#{n.rodada}</Td>
                    <Td style={{ fontSize: 12 }}>{n.data_negociacao}</Td>
                    <Td style={{ fontSize: 12 }}>{n.canal || '—'}</Td>
                    <Td style={{ fontSize: 11, color: colors.textDim }}>{n.protocolo || '—'}</Td>
                    <Td style={{ color: colors.danger, fontWeight: 600 }}>{n.valor_proposto ? fmt(n.valor_proposto) : '—'}</Td>
                    <Td>{n.desconto_pct ? `${n.desconto_pct}%` : '—'}</Td>
                    <Td style={{ textAlign: 'center' }}>{n.parcelas_propostas || '—'}</Td>
                    <Td>
                      <Badge
                        text={n.resultado || 'EM_ANDAMENTO'}
                        color={n.resultado === 'ACEITO' ? colors.success : n.resultado === 'RECUSADO' ? colors.danger : colors.warning}
                      />
                    </Td>
                    <Td style={{ fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={n.proxima_acao}>
                      {n.proxima_acao || '—'}
                      {n.data_proxima_acao && <span style={{ color: colors.textDim, marginLeft: 4 }}>({n.data_proxima_acao})</span>}
                    </Td>
                  </TrHover>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* ===== CONTAS A PAGAR ===== */}
      {activeTab === 'contas' && (
        <div>
          {contasPagar.length === 0 ? (
            <EmptyState message="Nenhuma conta a pagar vinculada" icon={DollarSign} />
          ) : (
            <>
              <div style={{ marginBottom: 12, fontSize: 13, color: colors.textMuted }}>
                {contasPagar.length} parcela(s) · Total em aberto: <strong style={{ color: colors.danger }}>
                  {fmt(contasPagar.filter(c => c.status !== 'PAGO').reduce((s, c) => s + (c.valor || 0), 0))}
                </strong>
              </div>
              <Table>
                <thead><tr>
                  <Th>Vencimento</Th><Th>Descrição</Th><Th>Tipo</Th><Th>Valor</Th><Th>Status</Th><Th>Parcela</Th>
                </tr></thead>
                <tbody>
                  {contasPagar.map(c => (
                    <TrHover key={c.id} style={c.status === 'PAGO' ? { opacity: 0.5 } : {}}>
                      <Td style={{ fontSize: 12, fontWeight: 600, color: new Date(c.data_vencimento) < new Date() && c.status !== 'PAGO' ? colors.danger : colors.text }}>
                        {c.data_vencimento}
                      </Td>
                      <Td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.descricao}</Td>
                      <Td>
                        <span style={{ fontSize: 11, background: colors.bg, border: `1px solid ${colors.border}`, borderRadius: 3, padding: '2px 6px', color: colors.textDim }}>
                          {c.tipo}
                        </span>
                      </Td>
                      <Td style={{ fontWeight: 700, color: colors.danger }}>{fmt(c.valor)}</Td>
                      <Td>
                        <Badge text={c.status} color={c.status === 'PAGO' ? colors.success : c.status === 'CANCELADO' ? colors.textMuted : colors.warning} />
                      </Td>
                      <Td style={{ fontSize: 12, color: colors.textDim }}>
                        {c.parcela_numero && c.total_parcelas ? `${c.parcela_numero}/${c.total_parcelas}` : '—'}
                      </Td>
                    </TrHover>
                  ))}
                </tbody>
              </Table>
            </>
          )}
        </div>
      )}

      {activeTab === 'documentacao' && (
        <div>
          {/* Toggle: cartão parou de gerar fatura */}
          <div style={{ background: colors.card, border: `1px solid ${cartao?.sem_fatura ? '#f97316' : colors.border}`, borderRadius: 12, padding: 16, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 240 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: colors.text }}>Cartão parou de gerar fatura</div>
              <div style={{ fontSize: 12, color: colors.textMuted, marginTop: 3 }}>
                {cartao?.sem_fatura
                  ? 'Ligado: este cartão NÃO aparece mais no controle de faturas. O acompanhamento é por comprovantes abaixo.'
                  : 'Desligado: o cartão ainda é cobrado no controle de faturas. Ligue para cartões em negociação que pararam de emitir fatura.'}
              </div>
            </div>
            <button onClick={toggleSemFatura} disabled={togglingSemFatura}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                background: cartao?.sem_fatura ? '#f97316' : colors.border, color: cartao?.sem_fatura ? '#fff' : colors.textMuted }}>
              {togglingSemFatura ? '...' : cartao?.sem_fatura ? '✓ Sem fatura (ligado)' : 'Marcar como sem fatura'}
            </button>
          </div>

          {/* Form: novo comprovante */}
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: colors.text, marginBottom: 12 }}>Adicionar comprovante (proposta, acordo, print da dívida...)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Descrição"><Input value={compForm.descricao} placeholder="Ex: Proposta de acordo, print da dívida..." onChange={e => setCompForm(f => ({ ...f, descricao: e.target.value }))} /></FormField>
              <FormField label="Arquivo (print / PDF)"><input type="file" accept=".pdf,image/*" onChange={e => setCompForm(f => ({ ...f, file: e.target.files[0] || null }))} style={{ fontSize: 13, color: colors.text }} /></FormField>
              <FormField label="Valor da dívida (R$)"><Input type="number" step="0.01" value={compForm.valor_divida} onChange={e => setCompForm(f => ({ ...f, valor_divida: e.target.value }))} /></FormField>
              <FormField label="Desconto (R$)"><Input type="number" step="0.01" value={compForm.desconto} onChange={e => setCompForm(f => ({ ...f, desconto: e.target.value }))} /></FormField>
              <FormField label="Mês de referência"><Input type="month" value={compForm.mes_referencia} onChange={e => setCompForm(f => ({ ...f, mes_referencia: e.target.value }))} /></FormField>
              <FormField label="Data do comprovante"><Input type="date" value={compForm.data_comprovante} onChange={e => setCompForm(f => ({ ...f, data_comprovante: e.target.value }))} /></FormField>
            </div>
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <Button onClick={addComprovante} disabled={savingComp}>{savingComp ? 'Salvando...' : '+ Adicionar comprovante'}</Button>
            </div>
          </div>

          {/* Lista de comprovantes */}
          {comprovantes.length === 0 ? (
            <EmptyState message="Nenhum comprovante ainda" icon={FileText} />
          ) : (
            <Table>
              <thead><tr>
                <Th>Data</Th><Th>Mês</Th><Th>Descrição</Th><Th>Dívida</Th><Th>Desconto</Th><Th>Arquivo</Th><Th>Ações</Th>
              </tr></thead>
              <tbody>
                {comprovantes.map(c => (
                  <TrHover key={c.id}>
                    <Td style={{ fontSize: 12 }}>{c.data_comprovante || '—'}</Td>
                    <Td style={{ fontSize: 12 }}>{c.mes_referencia || '—'}</Td>
                    <Td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.descricao || '—'}</Td>
                    <Td style={{ fontWeight: 600 }}>{c.valor_divida ? fmt(c.valor_divida) : '—'}</Td>
                    <Td style={{ color: colors.success }}>{c.desconto ? fmt(c.desconto) : '—'}</Td>
                    <Td>{c.tem_arquivo
                      ? <a href={`${API}/cartoes/${cartaoId}/comprovantes/${c.id}/arquivo`} target="_blank" rel="noreferrer" style={{ color: colors.primary, fontSize: 12 }}>{c.nome_arquivo ? c.nome_arquivo.slice(0, 22) : 'ver'}</a>
                      : '—'}</Td>
                    <Td><button onClick={() => delComprovante(c.id)} style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12 }}>Remover</button></Td>
                  </TrHover>
                ))}
              </tbody>
            </Table>
          )}
        </div>
      )}

      {/* ===== MODAL UPLOAD ===== */}
      <Modal isOpen={uploadModal} onClose={() => setUploadModal(false)} title="Enviar Fatura PDF">
        <FormField label="Mês de Referência">
          <Input type="month" value={uploadMes} onChange={e => setUploadMes(e.target.value)} />
        </FormField>
        <FormField label="Senha do PDF (opcional — deixe em branco para usar CPF/CEP automático)">
          <Input
            type="text"
            placeholder="Ex: 1234 ou 12345"
            value={uploadSenha}
            onChange={e => setUploadSenha(e.target.value)}
          />
        </FormField>
        <FormField label="💰 Valor TOTAL da fatura (opcional — sobrescreve OCR)">
          <Input
            type="text"
            placeholder="Ex: 17.118,83 — copie do PAGAMENTO TOTAL do PDF"
            value={uploadValorManual}
            onChange={e => setUploadValorManual(e.target.value)}
          />
          <div style={{ fontSize: 11, color: colors.textDim, marginTop: 4 }}>
            Se você digitar um valor aqui, ele será usado como o "Total da fatura" — o OCR não vai mais decidir. Os lançamentos individuais ainda vêm do PDF.
          </div>
        </FormField>
        <FormField label="Arquivo PDF">
          <div
            onClick={() => fileRef.current?.click()}
            style={{
              border: `2px dashed ${colors.border}`, borderRadius: 8, padding: '24px 16px',
              textAlign: 'center', cursor: 'pointer', color: colors.textMuted, fontSize: 13,
              background: uploadFile ? `${colors.success}08` : 'transparent',
            }}
          >
            <Upload size={24} style={{ marginBottom: 8, opacity: 0.5 }} />
            <div>{uploadFile ? `✓ ${uploadFile.name}` : 'Clique ou arraste o PDF aqui'}</div>
            <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }}
              onChange={e => setUploadFile(e.target.files[0])} />
          </div>
        </FormField>

        {uploadResult && (
          <div style={{ background: `${colors.success}12`, border: `1px solid ${colors.success}30`, borderRadius: 8, padding: 12, fontSize: 13 }}>
            ✓ Upload realizado.
            {uploadResult.processResult?.total_transacoes != null
              ? ` Claude extraiu ${uploadResult.processResult.total_transacoes} lançamentos · ${fmt(uploadResult.processResult.valor_total)}`
              : uploadResult.processResult?.error
                ? ` Processamento automático falhou: ${uploadResult.processResult.error}`
                : ' Aguardando processamento.'}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setUploadModal(false)}>Fechar</Button>
          <Button onClick={handleUpload} disabled={uploading || !uploadFile}>
            {uploading ? 'Enviando...' : 'Enviar e Processar'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
